import type { AnalyticsListener } from '../types/listener.js';
import type { EventEnvelope } from '../types/envelope.js';

export interface CustomApiListenerConfig {
  /** Endpoint URL for POST (e.g. https://api.example.com/analytics/events). */
  endpoint: string;
  /** Optional API key or bearer token. */
  apiKey?: string;
  /** Optional: batch size before sending. Default 1 (no batching). */
  batchSize?: number;
  /** Optional: max ms to wait before sending a partial batch. Default 5000. */
  flushIntervalMs?: number;
}

const DEFAULT_BATCH_SIZE = 1;
const DEFAULT_FLUSH_INTERVAL_MS = 5000;

/**
 * Listener that POSTs events to a configurable endpoint.
 * Optional batching: accumulates events and sends when batchSize or flushIntervalMs is reached.
 */
export function createCustomApiListener(config: CustomApiListenerConfig): AnalyticsListener {
  const endpoint = config.endpoint;
  const apiKey = config.apiKey;
  const batchSize = config.batchSize ?? DEFAULT_BATCH_SIZE;
  const flushIntervalMs = config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;

  let batch: EventEnvelope[] = [];
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleFlush(): void {
    if (flushTimer != null) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      sendBatch();
    }, flushIntervalMs);
  }

  function sendBatch(): void {
    if (batch.length === 0) return;
    const toSend = [...batch];
    batch = [];
    const body = JSON.stringify(toSend.length === 1 ? toSend[0] : { events: toSend });
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
    if (typeof fetch !== 'undefined') {
      fetch(endpoint, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      }).catch(() => {});
    }
  }

  return {
    init(_config: unknown): void {
      // config was passed at createCustomApiListener
    },
    track(envelope: EventEnvelope): void {
      try {
        batch.push(envelope);
        if (batch.length >= batchSize) {
          sendBatch();
        } else if (flushIntervalMs > 0) {
          scheduleFlush();
        }
      } catch {
        // swallow
      }
    },
    flush(): Promise<void> {
      if (flushTimer != null) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      sendBatch();
      return Promise.resolve();
    },
    teardown(): void {
      if (flushTimer != null) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      batch.length = 0;
    },
  };
}
